import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    IconButton,
    Stack,
    Chip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    CalendarToday as CalendarTodayIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Refresh as RefreshIcon,
    DateRange as DateRangeIcon,
    Today as TodayIcon
} from '@mui/icons-material';
import { amenityAPI, amenityScheduleAPI, amenityTypeAPI } from '../../services/api';
import { formatDateForInput, parseDateOnlyString } from '../../utils/dateUtils';
import BookAmenityDialog from '../../components/amenities/BookAmenityDialog';
import { Amenity, AmenityType } from '../../types';

// Types
interface AmenitySlot {
    startTime: string;
    endTime: string;
    status: string;
    bookedBy?: string;
    bookedByEmail?: string;
    bookingId?: string;
    pricingTier?: string;
    programName?: string;
    programInstanceId?: string;
    coachingSessionName?: string;
}

const AmenityBookingPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const calendarContainerRef = useRef<HTMLDivElement>(null);

    // State management
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableAmenityTypes, setAvailableAmenityTypes] = useState<AmenityType[]>([]);
    const [selectedAmenityType, setSelectedAmenityType] = useState<string>('');
    const [availableAmenities, setAvailableAmenities] = useState<Amenity[]>([]);
    const [filteredAmenities, setFilteredAmenities] = useState<Amenity[]>([]);
    const [amenitySlots, setAmenitySlots] = useState<{ [amenityId: string]: AmenitySlot[] }>({});
    const [amenitySlotDurations, setAmenitySlotDurations] = useState<{ [amenityId: string]: number }>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
    const [tempSelectedDate, setTempSelectedDate] = useState<string>('');

    // Booking dialog state
    const [openBookingDialog, setOpenBookingDialog] = useState(false);
    const [bookingDialogAmenity, setBookingDialogAmenity] = useState<Amenity | null>(null);
    const [bookingDialogDate, setBookingDialogDate] = useState<Date | null>(null);
    const [bookingDialogSlot, setBookingDialogSlot] = useState<{ startTime: string; endTime: string } | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Fetch amenity types and amenities when component mounts
    useEffect(() => {
        fetchAmenityTypes();
        fetchAmenities();
    }, []);

    // Filter amenities when type selection changes
    useEffect(() => {
        if (selectedAmenityType) {
            const filtered = availableAmenities.filter(a => a.type === selectedAmenityType);
            setFilteredAmenities(filtered);
        } else {
            setFilteredAmenities([]);
        }
    }, [selectedAmenityType, availableAmenities]);

    // Fetch calendar data when dependencies change
    useEffect(() => {
        if (filteredAmenities.length > 0) {
            fetchCalendarData();
        }
    }, [selectedDate, filteredAmenities]);

    // Auto-scroll on mount or when data changes
    useEffect(() => {
        if (Object.keys(amenitySlots).length > 0 && filteredAmenities.length > 0) {
            // Use a longer timeout to ensure DOM is fully rendered
            const timer = setTimeout(() => {
                if (selectedDate.toDateString() === new Date().toDateString()) {
                    scrollToCurrentTime();
                } else {
                    scrollToFirstAvailableSlot();
                }
            }, 800);

            return () => clearTimeout(timer);
        }
    }, [amenitySlots, selectedDate, filteredAmenities]);

    const fetchAmenityTypes = async () => {
        try {
            const response = await amenityTypeAPI.getAll();
            const types = response.data || [];
            // Filter only active amenity types
            const activeTypes = types.filter((type: AmenityType) => type.active);
            setAvailableAmenityTypes(activeTypes);

            // Auto-select first type if available
            if (activeTypes.length > 0) {
                setSelectedAmenityType(activeTypes[0].name);
            }
        } catch (error: any) {
            console.error('Error fetching amenity types:', error);
            setError('Failed to fetch amenity types');
        }
    };

    const fetchAmenities = async () => {
        try {
            const response = await amenityAPI.getAmenities();
            const amenities = response.data || [];
            setAvailableAmenities(amenities);
        } catch (error: any) {
            console.error('Error fetching amenities:', error);
            setError('Failed to fetch amenities');
        }
    };

    const fetchCalendarData = async () => {
        setLoading(true);
        setError(null);

        try {
            const dateString = formatDateForInput(selectedDate);
            const slotsData: { [amenityId: string]: AmenitySlot[] } = {};
            const slotDurations: { [amenityId: string]: number } = {};

            // Fetch slots and schedules for each filtered amenity
            await Promise.all(
                filteredAmenities.map(async (amenity) => {
                    try {
                        // Fetch available slots
                        const response = await amenityScheduleAPI.getAvailableSlots(amenity.id, dateString);
                        const slots = response.data?.slots || response.data || [];

                        // Filter out unavailable and unscheduled slots
                        const scheduledSlots = Array.isArray(slots)
                            ? slots.filter((slot: any) =>
                                slot.status && slot.status !== 'UNAVAILABLE' && slot.status !== 'OUT_OF_SCHEDULE'
                            )
                            : [];

                        slotsData[amenity.id] = scheduledSlots;

                        // Fetch min booking duration
                        try {
                            const durationResponse = await amenityScheduleAPI.getMinBookingDuration(amenity.id);
                            slotDurations[amenity.id] = durationResponse.data?.minBookingDurationMinutes || 30;
                        } catch (schedError) {
                            console.error(`Error fetching min booking duration for amenity ${amenity.id}:`, schedError);
                            slotDurations[amenity.id] = 30; // Default to 30 minutes
                        }
                    } catch (error) {
                        console.error(`Error fetching slots for amenity ${amenity.id}:`, error);
                        slotsData[amenity.id] = [];
                        slotDurations[amenity.id] = 30; // Default to 30 minutes
                    }
                })
            );

            setAmenitySlots(slotsData);
            setAmenitySlotDurations(slotDurations);
        } catch (error: any) {
            console.error('Error fetching calendar data:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch calendar data';
            setError(errorMessage);
            setAmenitySlots({});
            setAmenitySlotDurations({});
        } finally {
            setLoading(false);
        }
    };

    const navigateDate = (direction: 'prev' | 'next') => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));

        // Prevent going to past dates
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        newDate.setHours(0, 0, 0, 0);

        if (newDate >= today) {
            setSelectedDate(newDate);
        }
    };

    const goToToday = () => {
        setSelectedDate(new Date());
    };

    const openDatePickerDialog = () => {
        setTempSelectedDate(formatDateForInput(selectedDate));
        setOpenDatePicker(true);
    };

    const handleDatePickerConfirm = () => {
        if (tempSelectedDate) {
            const newDate = parseDateOnlyString(tempSelectedDate);

            // Prevent going to past dates
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            newDate.setHours(0, 0, 0, 0);

            if (newDate >= today) {
                setSelectedDate(newDate);
            } else {
                setError('Cannot select a date in the past');
            }
        }
        setOpenDatePicker(false);
    };

    const handleDatePickerCancel = () => {
        setOpenDatePicker(false);
        setTempSelectedDate('');
    };

    const isToday = (): boolean => {
        const today = new Date();
        return selectedDate.toDateString() === today.toDateString();
    };

    const isPastDate = (): boolean => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selected = new Date(selectedDate);
        selected.setHours(0, 0, 0, 0);
        return selected < today;
    };

    const scrollToCurrentTime = () => {
        if (!calendarContainerRef.current) return;

        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        // Calculate GCD-based block interval to align with time blocks
        let blockInterval = 30;
        if (Object.keys(amenitySlotDurations).length > 0) {
            const durations = Object.values(amenitySlotDurations);
            blockInterval = durations.reduce((acc, duration) => gcd(acc, duration), durations[0]);
        }

        // Round down to nearest time block
        const roundedMinute = Math.floor(currentMinute / blockInterval) * blockInterval;
        const timeString = `${currentHour.toString().padStart(2, '0')}:${roundedMinute.toString().padStart(2, '0')}`;

        // Try to find an element with the rounded time
        const timeElement = calendarContainerRef.current.querySelector(`[data-time="${timeString}"]`);

        if (timeElement) {
            const blockElement = timeElement.parentElement;
            if (blockElement) {
                const containerRect = calendarContainerRef.current.getBoundingClientRect();
                const blockRect = blockElement.getBoundingClientRect();
                const scrollTop = blockRect.top - containerRect.top + calendarContainerRef.current.scrollTop;

                calendarContainerRef.current.scrollTo({
                    top: Math.max(0, scrollTop - 100),
                    behavior: 'smooth'
                });
            }
        } else {
            // Fallback: try to find the closest time block before current time
            for (let tryMinute = roundedMinute; tryMinute >= 0; tryMinute -= blockInterval) {
                const fallbackTime = `${currentHour.toString().padStart(2, '0')}:${tryMinute.toString().padStart(2, '0')}`;
                const fallbackElement = calendarContainerRef.current.querySelector(`[data-time="${fallbackTime}"]`);

                if (fallbackElement) {
                    const blockElement = fallbackElement.parentElement;
                    if (blockElement) {
                        const containerRect = calendarContainerRef.current.getBoundingClientRect();
                        const blockRect = blockElement.getBoundingClientRect();
                        const scrollTop = blockRect.top - containerRect.top + calendarContainerRef.current.scrollTop;

                        calendarContainerRef.current.scrollTo({
                            top: Math.max(0, scrollTop - 100),
                            behavior: 'smooth'
                        });
                    }
                    break;
                }
            }
        }
    };

    const scrollToFirstAvailableSlot = () => {
        if (!calendarContainerRef.current) return;

        // Find the first available slot across all amenities
        const availableTimes: string[] = [];

        // Iterate through all amenity slots to find the earliest available slot
        Object.values(amenitySlots).forEach(slots => {
            slots.forEach(slot => {
                if (slot.status?.toUpperCase() === 'AVAILABLE' && slot.startTime) {
                    availableTimes.push(slot.startTime);
                }
            });
        });

        // Sort and get the earliest
        let earliestAvailableTime: string | undefined = availableTimes.length > 0
            ? availableTimes.sort()[0]
            : undefined;

        // If we found an available slot, scroll to it
        if (earliestAvailableTime) {
            const timeString = earliestAvailableTime.substring(0, 5); // Get HH:MM format
            const timeElement = calendarContainerRef.current.querySelector(`[data-time="${timeString}"]`);

            if (timeElement) {
                const blockElement = timeElement.parentElement;
                if (blockElement) {
                    const containerRect = calendarContainerRef.current.getBoundingClientRect();
                    const blockRect = blockElement.getBoundingClientRect();
                    const scrollTop = blockRect.top - containerRect.top + calendarContainerRef.current.scrollTop;

                    calendarContainerRef.current.scrollTo({
                        top: Math.max(0, scrollTop - 100),
                        behavior: 'smooth'
                    });
                }
            }
            return;
        }

        // If no available slots, scroll to the first time block with any slot
        const allTimes: string[] = [];

        Object.values(amenitySlots).forEach(slots => {
            slots.forEach(slot => {
                if (slot.startTime) {
                    allTimes.push(slot.startTime);
                }
            });
        });

        const earliestSlotTime = allTimes.length > 0 ? allTimes.sort()[0] : undefined;

        if (earliestSlotTime) {
            const timeString = earliestSlotTime.substring(0, 5);
            const timeElement = calendarContainerRef.current.querySelector(`[data-time="${timeString}"]`);

            if (timeElement) {
                const blockElement = timeElement.parentElement;
                if (blockElement) {
                    const containerRect = calendarContainerRef.current.getBoundingClientRect();
                    const blockRect = blockElement.getBoundingClientRect();
                    const scrollTop = blockRect.top - containerRect.top + calendarContainerRef.current.scrollTop;

                    calendarContainerRef.current.scrollTo({
                        top: Math.max(0, scrollTop - 100),
                        behavior: 'smooth'
                    });
                }
            }
        }
    };

    const getSlotStatusColor = (status: string) => {
        const statusUpper = status?.toUpperCase();
        switch (statusUpper) {
            case 'AVAILABLE': return '#c8e6c9'; // Light green
            case 'BOOKED': return '#ffcdd2'; // Light red/pink
            case 'ON_HOLD': return '#ffe0b2'; // Light orange
            case 'UNAVAILABLE': return '#f5f5f5'; // Light gray
            default: return '#f5f5f5';
        }
    };

    const getSlotTextColor = () => {
        return '#333333'; // Dark text for light backgrounds
    };

    // Helper function to calculate GCD (Greatest Common Divisor)
    const gcd = (a: number, b: number): number => {
        return b === 0 ? a : gcd(b, a % b);
    };

    const generateTimeBlocks = () => {
        // Find the GCD of all slot durations to ensure all slots align with time blocks
        let blockInterval = 30; // Default
        if (Object.keys(amenitySlotDurations).length > 0) {
            const durations = Object.values(amenitySlotDurations);
            blockInterval = durations.reduce((acc, duration) => gcd(acc, duration), durations[0]);
        }

        const blocks = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += blockInterval) {
                const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });

                blocks.push({ time, displayTime });
            }
        }
        return blocks;
    };

    const getSlotsForTimeSlot = (amenityId: string, timeSlot: string): AmenitySlot[] => {
        const slots = amenitySlots[amenityId] || [];
        return slots.filter(slot => {
            if (!slot.startTime || !slot.endTime) return false;

            // Parse time for comparison - convert to minutes since midnight
            const timeToMinutes = (time: string): number => {
                const [hours, minutes] = time.substring(0, 5).split(':').map(Number);
                return hours * 60 + minutes;
            };

            const blockStartMinutes = timeToMinutes(timeSlot);
            const slotStartMinutes = timeToMinutes(slot.startTime);

            // Only show slot at its start time to avoid duplicates
            return blockStartMinutes === slotStartMinutes;
        });
    };

    const getSlotSpan = (slot: AmenitySlot): number => {
        // Calculate how many time blocks this slot spans
        const timeToMinutes = (time: string): number => {
            const [hours, minutes] = time.substring(0, 5).split(':').map(Number);
            return hours * 60 + minutes;
        };

        const slotStartMinutes = timeToMinutes(slot.startTime);
        const slotEndMinutes = timeToMinutes(slot.endTime);
        const slotDurationMinutes = slotEndMinutes - slotStartMinutes;

        // Calculate GCD-based block interval (same logic as generateTimeBlocks)
        let blockInterval = 30;
        if (Object.keys(amenitySlotDurations).length > 0) {
            const durations = Object.values(amenitySlotDurations);
            blockInterval = durations.reduce((acc, duration) => gcd(acc, duration), durations[0]);
        }

        return Math.ceil(slotDurationMinutes / blockInterval);
    };

    const handleSlotClick = (amenityId: string, slot: AmenitySlot) => {
        // If slot is booked/on-hold and has a bookingId, navigate to booking detail page
        if (slot.bookingId && (slot.status?.toUpperCase() === 'BOOKED' || slot.status?.toUpperCase() === 'ON_HOLD' || slot.status?.toUpperCase() === 'OUT_OF_SCHEDULE')) {
            navigate(`/amenities/${amenityId}/bookings/${slot.bookingId}`);
        }
        // If slot is available, open booking dialog
        else if (slot.status?.toUpperCase() === 'AVAILABLE') {
            const amenity = filteredAmenities.find(a => a.id === amenityId);
            if (amenity) {
                setBookingDialogAmenity(amenity);
                setBookingDialogDate(selectedDate);
                setBookingDialogSlot({ startTime: slot.startTime, endTime: slot.endTime });
                setOpenBookingDialog(true);
            }
        }
    };

    const getTooltipContent = (amenity: Amenity, slot: AmenitySlot) => {
        return (
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {amenity.name}
                </Typography>
                <Typography variant="body2">
                    Status: {slot.status}
                </Typography>
                <Typography variant="body2">
                    Time: {slot.startTime} - {slot.endTime}
                </Typography>
                {slot.bookedBy && (
                    <Typography variant="body2">
                        Booked by: {slot.bookedBy}
                    </Typography>
                )}
                {slot.bookedByEmail && (
                    <Typography variant="body2">
                        Email: {slot.bookedByEmail}
                    </Typography>
                )}
                {slot.coachingSessionName && (
                    <Typography variant="body2">
                        Coaching Session: {slot.coachingSessionName}
                    </Typography>
                )}
                {slot.programName && (
                    <Typography variant="body2">
                        Program: {slot.programName}
                    </Typography>
                )}
                {slot.pricingTier && (
                    <Typography variant="body2">
                        Pricing: {slot.pricingTier.charAt(0).toUpperCase() + slot.pricingTier.slice(1).replace('-', ' ')}
                    </Typography>
                )}
            </Box>
        );
    };

    const renderDailyView = () => {
        const timeBlocks = generateTimeBlocks();

        if (filteredAmenities.length === 0) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        {selectedAmenityType
                            ? `No amenities found for type: ${selectedAmenityType}`
                            : 'Please select an amenity type to view the calendar'}
                    </Typography>
                </Box>
            );
        }

        // Mobile-optimized slot height
        const slotHeight = isMobile ? 50 : 60;

        return (
            <Box
                ref={calendarContainerRef}
                sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'auto',
                    boxShadow: 1,
                    maxHeight: isMobile ? '60vh' : '70vh'
                }}
            >
                {/* Header with amenity names */}
                <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper' }}>
                    <Box
                        sx={{
                            width: isMobile ? 60 : 80,
                            p: 1,
                            borderRight: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                            bgcolor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                            Time
                        </Typography>
                    </Box>
                    {filteredAmenities.map((amenity, index) => (
                        <Box
                            key={amenity.id}
                            sx={{
                                minWidth: isMobile ? 120 : 180,
                                flex: 1,
                                p: 1,
                                borderRight: index < filteredAmenities.length - 1 ? 1 : 0,
                                borderBottom: 1,
                                borderColor: 'divider',
                                bgcolor: 'grey.100',
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                {amenity.name}
                            </Typography>
                            {amenity.type && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                                    {amenity.type}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>

                {/* Time slots */}
                {timeBlocks.map((block, blockIndex) => (
                    <Box
                        key={blockIndex}
                        sx={{
                            display: 'flex',
                            borderBottom: 1,
                            borderColor: 'divider',
                            minHeight: slotHeight,
                            '&:hover': { bgcolor: 'grey.50' }
                        }}
                    >
                        <Box
                            sx={{
                                width: isMobile ? 60 : 80,
                                p: 1,
                                borderRight: 1,
                                borderColor: 'divider',
                                bgcolor: 'grey.100',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.65rem' : '0.75rem' }}
                                data-time={block.time}
                            >
                                {block.displayTime}
                            </Typography>
                        </Box>
                        {filteredAmenities.map((amenity, amenityIndex) => (
                            <Box
                                key={amenity.id}
                                sx={{
                                    minWidth: isMobile ? 120 : 180,
                                    flex: 1,
                                    p: 0.5,
                                    borderRight: amenityIndex < filteredAmenities.length - 1 ? 1 : 0,
                                    borderColor: 'divider',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    position: 'relative'
                                }}
                            >
                                {getSlotsForTimeSlot(amenity.id, block.time).map((slot, slotIndex) => {
                                    const slotSpan = getSlotSpan(slot);
                                    const adjustedSlotHeight = slotSpan * slotHeight;

                                    return (
                                        <Tooltip
                                            key={slotIndex}
                                            title={getTooltipContent(amenity, slot)}
                                            arrow
                                            placement="top"
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        maxWidth: 300,
                                                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                                                        color: 'white',
                                                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                        padding: 2
                                                    }
                                                }
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    left: 4,
                                                    right: 4,
                                                    height: `${adjustedSlotHeight - 8}px`,
                                                    bgcolor: getSlotStatusColor(slot.status),
                                                    color: getSlotTextColor(),
                                                    cursor: 'pointer',
                                                    borderRadius: 1,
                                                    p: isMobile ? 0.5 : 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'flex-start',
                                                    zIndex: 1,
                                                    '&:hover': {
                                                        boxShadow: 2,
                                                        transform: 'translateY(-1px)',
                                                        transition: 'all 0.2s ease-in-out',
                                                        zIndex: 2
                                                    }
                                                }}
                                                onClick={() => handleSlotClick(amenity.id, slot)}
                                            >
                                                <Typography variant="caption" sx={{ fontSize: isMobile ? '0.6rem' : '0.7rem', lineHeight: 1.2, fontWeight: 'bold' }}>
                                                    {slot.startTime} - {slot.endTime}
                                                </Typography>
                                                {slot.pricingTier && (
                                                    <Typography variant="caption" sx={{ fontSize: isMobile ? '0.55rem' : '0.65rem', lineHeight: 1.2, mt: 0.25 }}>
                                                        {slot.pricingTier.charAt(0).toUpperCase() + slot.pricingTier.slice(1).replace('-', ' ')}
                                                    </Typography>
                                                )}
                                                {slot.bookedBy && (
                                                    <Typography variant="caption" sx={{ fontSize: isMobile ? '0.55rem' : '0.65rem', lineHeight: 1.2, mt: 0.25, fontWeight: 'bold' }}>
                                                        {slot.bookedBy}
                                                    </Typography>
                                                )}
                                                {slot.coachingSessionName && !isMobile && (
                                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1.2, mt: 0.25, fontStyle: 'italic', opacity: 0.9 }}>
                                                        Session: {slot.coachingSessionName}
                                                    </Typography>
                                                )}
                                                {slot.programName && !isMobile && (
                                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1.2, mt: 0.25, fontStyle: 'italic', opacity: 0.9 }}>
                                                        Program: {slot.programName}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>
        );
    };

    const getViewTitle = () => {
        return selectedDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            weekday: 'short'
        });
    };

    // Get minimum date for date picker (today)
    const getMinDate = () => {
        return formatDateForInput(new Date());
    };

    return (
        <Container maxWidth="xl" sx={{ px: isMobile ? 1 : 3 }}>
            <Box sx={{ mt: isMobile ? 2 : 4, mb: isMobile ? 2 : 4 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 'bold' }}>
                            <CalendarTodayIcon sx={{ mr: isMobile ? 1 : 2, verticalAlign: 'middle' }} />
                            Book Amenities
                        </Typography>
                        {!isMobile && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Select an amenity type to view available bookings
                            </Typography>
                        )}
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchCalendarData}
                        disabled={loading || filteredAmenities.length === 0}
                        size={isMobile ? 'small' : 'medium'}
                    >
                        Refresh
                    </Button>
                </Box>

                {/* Controls */}
                <Paper sx={{ p: isMobile ? 2 : 3, mb: 2 }}>
                    <Stack spacing={2}>
                        {/* Amenity Type Filter */}
                        <FormControl fullWidth>
                            <InputLabel id="amenity-type-select-label">Amenity Type</InputLabel>
                            <Select
                                labelId="amenity-type-select-label"
                                id="amenity-type-select"
                                value={selectedAmenityType}
                                label="Amenity Type"
                                onChange={(e) => setSelectedAmenityType(e.target.value)}
                            >
                                {availableAmenityTypes.map((type) => (
                                    <MenuItem key={type.id} value={type.name}>
                                        {type.name}
                                        {type.description && (
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                - {type.description}
                                            </Typography>
                                        )}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Date Navigation */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 1 : 2, flexWrap: 'wrap' }}>
                            <IconButton
                                onClick={() => navigateDate('prev')}
                                size="small"
                                disabled={isPastDate()}
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ minWidth: isMobile ? 180 : 250, textAlign: 'center' }}>
                                {getViewTitle()}
                            </Typography>
                            <IconButton onClick={() => navigateDate('next')} size="small">
                                <ChevronRightIcon />
                            </IconButton>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={goToToday}
                                startIcon={<TodayIcon />}
                                disabled={isToday()}
                            >
                                {isMobile ? 'Today' : 'Today'}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={openDatePickerDialog}
                                startIcon={<DateRangeIcon />}
                            >
                                {isMobile ? 'Jump' : 'Jump'}
                            </Button>
                        </Box>
                    </Stack>
                </Paper>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Legend */}
                <Paper sx={{ p: isMobile ? 1.5 : 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                        Status Legend
                    </Typography>
                    <Stack direction="row" spacing={isMobile ? 1 : 2} flexWrap="wrap" sx={{ gap: 1 }}>
                        <Chip label="Available" size="small" sx={{ bgcolor: '#c8e6c9', color: '#333', fontSize: isMobile ? '0.65rem' : '0.75rem' }} />
                        <Chip label="Booked" size="small" sx={{ bgcolor: '#ffcdd2', color: '#333', fontSize: isMobile ? '0.65rem' : '0.75rem' }} />
                        <Chip label="On Hold" size="small" sx={{ bgcolor: '#ffe0b2', color: '#333', fontSize: isMobile ? '0.65rem' : '0.75rem' }} />
                        <Chip label="Unavailable" size="small" sx={{ bgcolor: '#f5f5f5', color: '#333', fontSize: isMobile ? '0.65rem' : '0.75rem' }} />
                    </Stack>
                    {!isMobile && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            💡 Click on an available slot to book
                        </Typography>
                    )}
                </Paper>

                {/* Calendar Content */}
                <Paper sx={{ p: isMobile ? 1 : 3 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        renderDailyView()
                    )}
                </Paper>

                {/* Date Picker Dialog */}
                <Dialog open={openDatePicker} onClose={handleDatePickerCancel} maxWidth="xs" fullWidth>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DateRangeIcon sx={{ mr: 1 }} />
                            Jump to Date
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                label="Select Date"
                                type="date"
                                value={tempSelectedDate}
                                onChange={(e) => setTempSelectedDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: getMinDate() }}
                                fullWidth
                                autoFocus
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDatePickerCancel}>Cancel</Button>
                        <Button
                            onClick={handleDatePickerConfirm}
                            variant="contained"
                            disabled={!tempSelectedDate}
                        >
                            Go to Date
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Booking Dialog */}
                <BookAmenityDialog
                    open={openBookingDialog}
                    onClose={() => {
                        setOpenBookingDialog(false);
                        setBookingDialogAmenity(null);
                        setBookingDialogDate(null);
                        setBookingDialogSlot(null);
                    }}
                    amenity={bookingDialogAmenity}
                    onSuccess={(message) => {
                        setSuccessMessage(message || 'Booking created successfully!');
                        // Refresh calendar data to show the new booking
                        fetchCalendarData();
                    }}
                    preselectedDate={bookingDialogDate}
                    preselectedSlot={bookingDialogSlot}
                />

                {/* Success Snackbar */}
                {successMessage && (
                    <Alert
                        severity="success"
                        onClose={() => setSuccessMessage(null)}
                        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
                    >
                        {successMessage}
                    </Alert>
                )}
            </Box>
        </Container>
    );
};

export default AmenityBookingPage;
